/* Pure helpers for the sidebar: which groups are collapsed (remembered in localStorage, always inside
   try/catch because storage can be blocked) and the feature/command search. */
import type { FeatureDescriptor, FeatureCommand, FeatureGroup } from './feature';

export const NAV_GROUPS_KEY = 'cx.nav.closedGroups';

export function loadClosedGroups(): FeatureGroup[] {
  try {
    const raw = localStorage.getItem(NAV_GROUPS_KEY);
    const v = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(v) ? (v.filter((x) => typeof x === 'string') as FeatureGroup[]) : [];
  } catch {
    return [];
  }
}

export function saveClosedGroups(closed: FeatureGroup[]): void {
  try {
    localStorage.setItem(NAV_GROUPS_KEY, JSON.stringify(closed));
  } catch {
    /* storage blocked: the groups still work, they just are not remembered */
  }
}

export type NavMatch =
  | { kind: 'feature'; key: string; title: string; group: string; feature: FeatureDescriptor }
  | { kind: 'command'; key: string; title: string; group: string; feature: FeatureDescriptor; command: FeatureCommand };

/** Features whose title matches first, then their commands. Every word of the query must appear. */
export function matchNav(features: FeatureDescriptor[], query: string, limit = 10): NavMatch[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const hit = (...hay: (string | undefined)[]) => {
    const text = hay.filter(Boolean).join(' ').toLowerCase();
    return words.every((w) => text.includes(w));
  };
  const out: NavMatch[] = [];
  for (const f of features) if (hit(f.title, f.short, f.id)) out.push({ kind: 'feature', key: `f-${f.id}`, title: f.title, group: f.group, feature: f });
  for (const f of features) {
    for (const c of f.commands ?? []) {
      if (hit(c.title, ...(c.keywords ?? []))) out.push({ kind: 'command', key: `c-${f.id}-${c.id}`, title: c.title, group: f.title, feature: f, command: c });
    }
  }
  return out.slice(0, limit);
}

/** Features the Create hub gathers (the Create destination in the sidebar and the bottom bar). */
export const CREATE_IDS = ['build', 'slides', 'sheets', 'docs', 'media', 'design', 'social'];
